import type { ActorRefFrom, AnyActorLogic } from 'xstate';
import z from 'zod';
import { ERROR_MESSAGES } from '~/constants';
import { optionSchema } from '~/features/types';
import { isStartDateAllowed, START_DATE_IN_PAST_MESSAGE } from '../../../shared-steps/startDate';

export interface Context {
  loading: boolean;

  parentRef?: ActorRefFrom<AnyActorLogic>;
}

export type OnAddEvent = {
  type: 'ON_ADD';
  payload: { values: LaborMetricsFormValues };
};

export type OnUpdateEvent = {
  type: 'ON_UPDATE';
  payload: { values: LaborMetricsFormValues };
};

export type Events = OnAddEvent | OnUpdateEvent;

// [MCV-1524] A factory rather than a const: the past-date rule needs to know
// what start date is already on the record, so an existing listing whose
// start date has passed can still be edited. See ./shared-steps/startDate.
export const createLaborMetricsFormSchema = (persistedStartDate?: string) =>
  z
    .object({
      id: z.number().optional(),
      msaasListingId: z.number().optional(),
      startDate: z.iso.date(ERROR_MESSAGES.INVALID_DATE),
      laborReqType: optionSchema,
      endDate: z.string().optional(),
      numOfFtes: z.preprocess(
        (arg) => (arg === '' || arg == null ? null : Number(arg)),
        // [MCV-1547] Non-negative even when the field is not the active labor
        // type, so an unused-but-populated figure can't carry a negative.
        z.number().min(0, ERROR_MESSAGES.NON_NEGATIVE_INT).optional().nullable()
      ),
      fteShiftsPerYear: z.preprocess(
        (arg) => (arg === '' || arg == null ? null : Number(arg)),
        z.number().min(1, ERROR_MESSAGES.REQUIRED)
      ),
      hoursPerShift: z.preprocess(
        (arg) => (arg === '' || arg == null ? null : Number(arg)),
        z.number().min(1, ERROR_MESSAGES.REQUIRED)
      ),
      minRvusPerShift: z.preprocess(
        (arg) => (arg === '' || arg == null ? null : Number(arg)),
        // [MCV-1547] Non-negative even when the field is not the active labor
        // type, so an unused-but-populated figure can't carry a negative.
        z.number().min(0, ERROR_MESSAGES.NON_NEGATIVE_INT).optional().nullable()
      ),
      numRvusReq: z.preprocess(
        (arg) => (arg === '' || arg == null ? null : Number(arg)),
        // [MCV-1547] Non-negative even when the field is not the active labor
        // type, so an unused-but-populated figure can't carry a negative.
        z.number().min(0, ERROR_MESSAGES.NON_NEGATIVE_INT).optional().nullable()
      ),
    })
    // [MCV-1541][MCV-1545] These fields are conditionally required on the labor
    // requirement type, and the previous null-only checks let a 0 through — so
    // the asterisked "Number of Providers (FTEs)" and "Minimum RVUs per Shift"
    // never actually held the user on the step. Require a positive figure, and
    // separate "you left it blank" from "0 isn't a usable value" so the message
    // matches what the user did.
    .superRefine((schema, ctx) => {
      const requiredWhen: {
        active: boolean;
        path: 'numOfFtes' | 'minRvusPerShift' | 'numRvusReq';
        value: unknown;
      }[] = [
        { active: schema.laborReqType.value === 'FTE', path: 'numOfFtes', value: schema.numOfFtes },
        {
          active: schema.laborReqType.value === 'RVU',
          path: 'minRvusPerShift',
          value: schema.minRvusPerShift,
        },
        {
          active: schema.laborReqType.value === 'RVU',
          path: 'numRvusReq',
          value: schema.numRvusReq,
        },
      ];

      for (const { active, path, value } of requiredWhen) {
        if (!active) continue;
        if (value === undefined || value === null) {
          ctx.addIssue({ code: 'custom', message: ERROR_MESSAGES.REQUIRED, path: [path] });
        } else if (Number(value) <= 0) {
          ctx.addIssue({ code: 'custom', message: ERROR_MESSAGES.POSITIVE_INT, path: [path] });
        }
      }
    })
    .refine((schema) => isStartDateAllowed(schema.startDate, persistedStartDate), {
      message: START_DATE_IN_PAST_MESSAGE,
      path: ['startDate'],
    });

export type Scope = {
  context: Context;
  event: Events;
};

export type LaborMetricsFormValues = z.infer<ReturnType<typeof createLaborMetricsFormSchema>>;
